import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { useEffect, useState } from "react";
import theme from "./styles/theme";
import GlobalStyle from "./styles/GlobalStyle";
import Header from "./components/Header";
import LoginPage from "./pages/LoginPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import PremierAccesPage from "./pages/PremierAccesPage";
import Dashboard from "./pages/Dashboard";
import PlayerProfile from "./pages/PlayerProfile";
import EvaluationList from "./components/EvaluationList";
import EvaluationExercise from "./pages/EvaluationExercise";
import ManagePlayers from "./pages/ManagePlayers";
import ManageExercises from "./pages/ManageExercises";
import SessionsPage from "./pages/SessionsPage";
import PlayerEvolution from "./pages/PlayerEvolution";
import ManageTeams from "./pages/ManageTeams";
import CompoCreate from "./pages/CompoCreate";
import ManageVideos from "./pages/ManageVideos";
import VolleySchemaStudio from "./pages/VolleySchemaStudio";
import SchemaQuiz from "./pages/SchemaQuiz";
import AdminRotations from "./pages/AdminRotations";
import MatchTracker from "./pages/MatchTracker";
import CoachCards from "./pages/CoachCards";
import SuperAdminPasswords from "./pages/SuperAdminPasswords";
import SuperAdminQuizSettings from "./pages/SuperAdminQuizSettings";
import {
  Typography,
} from "@mui/material";

function App() {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user") || "null"));
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  const hasSuperAdminFlag = user?.is_superadmin === true || user?.is_superadmin === 1 || user?.is_superadmin === "1";
  const isSuperAdmin = hasSuperAdminFlag || ["staff", "superadmin"].includes(user?.role);
  const isAdmin = user?.role === "coach" || isSuperAdmin;

  useEffect(() => {
    const handleStorageChange = () => {
      setUser(JSON.parse(localStorage.getItem("user") || "null"));
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: any) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    setUser(null); // Met à jour immédiatement l'état global
  };

  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult: any) => {
        if (choiceResult.outcome === "accepted") {
          console.log("L'utilisateur a accepté l'installation de la PWA.");
        } else {
          console.log("L'utilisateur a refusé l'installation de la PWA.");
        }
        setDeferredPrompt(null);
      });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Router>
        <Header user={user} onLogout={handleLogout} />
        {deferredPrompt && (
          <div style={{ position: "fixed", bottom: 0, width: "100%", backgroundColor: "#673ab7", color: "#fff", textAlign: "center", padding: "10px", zIndex: 2000 }}>
            <p>Installer l'application pour une meilleure expérience.</p>
            <button onClick={handleInstallClick} style={{ backgroundColor: "#fff", color: "#673ab7", padding: "10px", borderRadius: "5px", border: "none", cursor: "pointer" }}>
              Installer
            </button>
          </div>
        )}
        <Routes>
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/premier-acces" element={<PremierAccesPage />} />
          <Route
            path="/dashboard"
            element={user?.role === "coach" ? <Dashboard /> : <Navigate to={`/player/${user?.id}`} />}
          />
          <Route
            path="/player/:id"
            element={user ? <PlayerProfile /> : <Navigate to="/login" />}
          />
          <Route
            path="/manage-players"
            element={user?.role === "coach" ? <ManagePlayers /> : <Navigate to={`/player/${user?.id}`} />}
          />
          <Route
            path="/manage-exercises"
            element={user?.role === "coach" ? <ManageExercises /> : <Navigate to={`/player/${user?.id}`} />}
          />
          <Route path="/evaluation" element={<EvaluationList />} />
          <Route path="/evaluation/exercise/:id" element={<EvaluationExercise />} />
          <Route path="/sessions" element={user?.role === "coach" ? <SessionsPage /> : <Navigate to="/login" />} />
          <Route path="/evolution" element={user?.role === "coach" ? <PlayerEvolution /> : <Navigate to="/login" />} />
          <Route path="/manage-teams" element={user?.role === "coach" ? <ManageTeams /> : <Navigate to="/login" />} />
          <Route path="/manage-videos" element={user?.role === "coach" ? <ManageVideos /> : <Navigate to="/login" />} />
          <Route path="/compo-create" element={user?.role === "coach" ? <CompoCreate /> : <Navigate to="/login" />} />
          <Route
            path="/schema-studio"
            element={user ? <VolleySchemaStudio isAdmin={isAdmin} /> : <Navigate to="/login" />}
          />
          <Route
            path="/schema-quiz"
            element={user ? <SchemaQuiz /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin-rotations"
            element={isAdmin ? <AdminRotations /> : <Navigate to="/login" />}
          />
          <Route
            path="/suivi-match"
            element={isAdmin ? <MatchTracker /> : <Navigate to="/login" />}
          />
          <Route
            path="/cartons"
            element={isAdmin ? <CoachCards /> : <Navigate to="/login" />}
          />
          <Route
            path="/superadmin-passwords"
            element={isSuperAdmin ? <SuperAdminPasswords /> : <Navigate to="/login" />}
          />
          <Route
            path="/superadmin-quiz-settings"
            element={isSuperAdmin ? <SuperAdminQuizSettings /> : <Navigate to="/login" />}
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <footer
          style={{
            width: '100vw',
            textAlign: 'center',
            color: theme.palette.background.paperDark,
            fontFamily: theme.typography.fontFamily,
            fontSize: '0.95rem',
            opacity: 0.9,
            padding: '10px 0',
            background: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
            zIndex: 1000,
            position: 'fixed',
            left: 0,
            bottom: 0,
          }}
        >
          <b>PlayerMetrics v1.10</b> by{" "}
          <a
            href="https://atoneo.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: theme.palette.secondary.main, textDecoration: "underline", fontWeight: "bold" }}
          >
            atoneo.com
          </a>{" "} 
          © 2025
          <br />
          <Typography
          style={{ color: theme.palette.warning.main, marginTop: '5px', fontWeight: 'bold' }}
          >Ecrire à Alex en cas de bug !</Typography>
        </footer>
      </Router>
    </ThemeProvider>
  );
}

export default App;
